import { InteractionFormValue } from '../../observation-interaction/interaction-form-value.model';
import { ScoreFormValue } from '../../observation-score/score-form-value.model';
import { TagGroupFormValue } from '../../observation-tag-group/tag-group-form-value.model';
import { ObservationFormValue } from '../observation-form-value.model';

import { FocusContainerValidationResult } from './focus-container-validation-result.model';
import { InteractionValidationResult } from './interaction-validation-result.model';
import { NoteValidationResult } from './note-validation-result.model';
import { ObservationValidationResult } from './observation-validation-result.model';
import { ScoreValidationResult } from './score-validation-result.model';
import { ValidationDialogArgs } from './validation-dialog-args.model';

function isScoreValid(
    score: ScoreFormValue,
    opts: { siblingNoteAdded: boolean },
): ScoreValidationResult {
    return {
        otdChosen: score.opportunityToDemonstrate != null,
        wttChosen: score.willingnessToTry != null,
        pdChosen: score.proficiencyDelivered != null,
        valid:
            (opts.siblingNoteAdded && score.opportunityToDemonstrate == null) ||
            score.opportunityToDemonstrate === false ||
            (score.opportunityToDemonstrate === true &&
                score.willingnessToTry === false) ||
            (score.opportunityToDemonstrate === true &&
                score.willingnessToTry === true &&
                score.proficiencyDelivered != null),
        trackingId: score.trackingId,
        trackingChanged: score.trackingChanged,
    };
}

function isInteractionValid(
    interaction: InteractionFormValue,
): InteractionValidationResult {
    const noteAdded =
        interaction.notes &&
        interaction.notes.length !== 0 &&
        interaction.notes.some((e) => !!e.contents);
    const scoreAdded =
        interaction.scoring &&
        interaction.scoring.length !== 0 &&
        interaction.scoring.some((e) => e.opportunityToDemonstrate != null);

    const notes = interaction.notes
        ? interaction.notes.map<NoteValidationResult>((e) => ({
              hasContent: !!e.contents,
              valid: (scoreAdded && !e.contents) || !!e.contents,
              trackingId: e.trackingId,
          }))
        : [];

    const scores = interaction.scoring
        ? interaction.scoring.map((e) =>
              isScoreValid(e, { siblingNoteAdded: noteAdded }),
          )
        : [];
    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
    const scoresValid = scores && scores.every((e) => e.valid);

    return {
        noteAdded,
        scoreAdded,
        notes,
        scores,
        valid: (noteAdded || scoreAdded) && scoresValid,
        trackingId: interaction.trackingId,
        trackingChanged: interaction.trackingChanged,
    };
}

function isFocusContainerValid(
    fc: TagGroupFormValue,
    opts: Opts,
): FocusContainerValidationResult {
    const interactions = fc.interactions.map((e) => isInteractionValid(e));

    let valid = true;
    if (opts.isPublishingFn()) {
        if (fc.behaviourId == null && !opts.unFocusedContainerIsVisibleFn()) {
            valid = true;
        } else if (
            !fc.trackingChanged &&
            fc.behaviourId != null &&
            !opts.isSavedObservationFn()
        ) {
            valid = true;
        } else {
            valid =
                fc.behaviourId != null &&
                fc.interactions &&
                fc.interactions.length !== 0 &&
                interactions.every((e) => e.valid);
        }
    }

    return {
        savedObservation: opts.isSavedObservationFn(),
        focusSelected: fc.behaviourId != null,
        hidden: fc.behaviourId == null && opts.unFocusedContainerIsVisibleFn(),
        interactions,
        valid,
        trackingId: fc.trackingId,
        trackingChanged: fc.trackingChanged,
    };
}

interface Opts {
    isPublishingFn: () => boolean;
    isSavedObservationFn: () => boolean;
    unFocusedContainerIsVisibleFn: () => boolean;
}

/** technically not a form validator, so it's not named observationValidator */
export function observationIsValid(
    o: ObservationFormValue,
    opts: Opts,
): ObservationValidationResult {
    let highlightAllFocusContainers = false;
    const focusContainers = o.tagGroups
        ? o.tagGroups.map((e) => isFocusContainerValid(e, opts))
        : [];
    let valid = focusContainers.every((e) => e.valid);

    const noneTouched = (
        o.tagGroups
            ? o.tagGroups.filter((e) =>
                  opts.unFocusedContainerIsVisibleFn()
                      ? true
                      : e.behaviourId != null,
              )
            : []
    ).every((e) => !opts.isSavedObservationFn() && !e.trackingChanged);
    if (valid && noneTouched) {
        valid = false;
        highlightAllFocusContainers = true;
    }

    // first invalid focus container, as the dialog will be specific to this one
    let dialogArgs: ValidationDialogArgs = {
        focusValid: false,
        interactionValid: false,
        wttInvalid: true,
        pdInvalid: true,
    };
    const fc = focusContainers.find((e) => !e.hidden && !e.valid);
    if (fc) {
        // this is done like this to match the previous behaviour, since the dialog itself has not been changed
        // first invalid interaction, as the dialog will be specific to this one
        const interaction = fc.interactions.find((e) => !e.valid);
        dialogArgs.focusValid = fc.focusSelected;
        dialogArgs.interactionValid =
            interaction != null &&
            (interaction.noteAdded || interaction.scoreAdded);
        if (dialogArgs.interactionValid) {
            // first invalid score, as the dialog will be specific to this one
            const score = interaction
                ? interaction.scores.find((e) => !e.valid)
                : null;
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            dialogArgs.wttInvalid =
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                score != null && score.otdChosen && !score.wttChosen;
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            dialogArgs.pdInvalid =
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                score != null &&
                score.otdChosen &&
                score.wttChosen &&
                !score.pdChosen;
        }
    } else if (focusContainers.length === 0 || highlightAllFocusContainers) {
        // this is done like this to match the previous behaviour, since the dialog itself has not been changed
        dialogArgs = {
            focusValid: true,
            interactionValid: false,
            wttInvalid: true,
            pdInvalid: true,
        };
    }
    const showDialog =
        dialogArgs &&
        (!dialogArgs.focusValid ||
            !dialogArgs.interactionValid ||
            dialogArgs.wttInvalid ||
            dialogArgs.pdInvalid);

    return {
        highlightAllFocusContainers,
        dialogArgs,
        showDialog,
        focusContainers,
        valid,
    };
}
